<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
// Import static assets properly for production
import clipflowLogoOnly from '/img/clipflow-logo-only.svg'

defineProps<{
  noSpacing?: boolean
}>()

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const searchQuery = ref('')

// Initialize sidebar state from localStorage
const isSidebarCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')

// Fetch user data on component mount if not available
onMounted(async () => {
  if (authStore.isAuthenticated && !authStore.currentUser) {
    await authStore.fetchCurrentUser()
  }
})

const sidebarNavItems = [
  { name: 'Home', icon: 'home', route: '/dashboard' },
]

// Check if navigation item is active based on current route
const isNavItemActive = (navRoute: string) => {
  if (navRoute === '/dashboard') {
    // Home tab is active for dashboard route and all project-related routes
    return route.path === '/dashboard' ||
           route.path.startsWith('/projects') ||
           route.path.startsWith('/clips') ||
           route.path.includes('voting') ||
           route.path.includes('user-voting')
  }
  return route.path === navRoute || route.path.startsWith(navRoute + '/')
}

const handleNavClick = (item: any) => {
  // Handle navigation
  console.log('Nav clicked:', item.name)
  router.push(item.route)
}

const handleSearch = () => {
  console.log('Search:', searchQuery.value)
}

const handleBack = () => {
  router.back()
}

const handleCreateProject = () => {
  // Emit event to parent component
  emit('create-project')
}

const toggleSidebar = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
  // Persist sidebar state to localStorage
  localStorage.setItem('sidebarCollapsed', String(isSidebarCollapsed.value))
}

// Define emits
const emit = defineEmits<{
  'create-project': []
}>()
</script>

<template>
  <div class="h-screen bg-gray-50 flex overflow-hidden">
    <!-- Sidebar -->
    <div
      class="bg-white shadow-sm border-r border-gray-200 flex flex-col transition-all duration-300 h-screen sticky top-0"
      :class="isSidebarCollapsed ? 'w-16' : 'w-64'"
      :style="{ minWidth: isSidebarCollapsed ? '4rem' : '16rem', maxWidth: isSidebarCollapsed ? '4rem' : '16rem' }"
    >
      <!-- Logo -->
      <div
        class="border-b border-gray-100 h-[71px] flex items-center justify-between"
        :class="isSidebarCollapsed ? 'px-3' : 'px-6'"
      >
          <!-- Logo - Hidden when collapsed -->
          <router-link
            v-show="!isSidebarCollapsed"
            to="/dashboard"
            class="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <img :src="clipflowLogoOnly" alt="Clipflow" class="h-8 w-8" />
            <span class="text-xl font-semibold text-gray-900">Clipflow</span>
          </router-link>

          <!-- Toggle Button -->
          <button
            @click="toggleSidebar"
            class="text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            :class="{
              'mx-auto p-1': isSidebarCollapsed,
              'p-2': !isSidebarCollapsed
            }"
            :title="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          >
            <img
              src="/img/arrow-left.svg"
              alt="Toggle"
              class="w-5 h-5 transition-transform duration-200"
              :class="{ 'rotate-180': isSidebarCollapsed }"
            />
          </button>
      </div>

      <!-- Navigation -->
      <nav
        class="flex-1 space-y-1"
        :class="isSidebarCollapsed ? 'p-3' : 'p-6'"
      >
        <!-- Create Project Button - Hidden when collapsed -->
        <button
          v-show="!isSidebarCollapsed"
          @click="handleCreateProject"
          class="w-full mb-4 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span>Create Project</span>
        </button>

        <!-- Home Navigation - Hidden when collapsed -->
        <div
          v-for="item in sidebarNavItems"
          v-show="!isSidebarCollapsed"
          :key="item.name"
          @click="handleNavClick(item)"
          :class="[
            'flex items-center space-x-4 px-0 py-3 cursor-pointer transition-colors',
            isNavItemActive(item.route)
              ? 'text-purple-600'
              : 'text-gray-600 hover:text-gray-900',
          ]"
        >
          <!-- Home Icon -->
          <svg
            v-if="item.icon === 'home'"
            class="w-6 h-6"
            :class="isNavItemActive(item.route) ? 'text-purple-600' : 'text-gray-500'"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>

          <span class="text-lg font-medium">{{ item.name }}</span>
        </div>
      </nav>

    </div>

    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col h-screen overflow-y-auto">
      <!-- Header -->
      <header class="bg-white shadow-sm border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div class="flex items-center justify-between">
          <!-- Left side with back button -->
          <!-- <div class="flex items-center space-x-4">
            <button
              @click="handleBack"
              class="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          </div> -->

          <!-- Center search bar -->
          <div class="flex-1 max-w-lg mx-8">
            <!-- <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  class="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                v-model="searchQuery"
                type="search"
                placeholder="Search for anything"
                class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                @keyup.enter="handleSearch"
              />
            </div> -->
          </div>

          <!-- Right side with user avatar -->
          <div class="flex items-center space-x-3">
            <div v-if="authStore.isUserLoading" class="flex items-center space-x-3">
              <div class="animate-pulse">
                <div class="h-8 w-8 bg-gray-300 rounded-full"></div>
              </div>
              <div class="animate-pulse">
                <div class="h-4 w-20 bg-gray-300 rounded"></div>
                <div class="h-3 w-24 bg-gray-300 rounded mt-1"></div>
              </div>
            </div>
            <div v-else-if="authStore.currentUser" class="flex items-center space-x-3">
              <div class="text-right">
                <p class="text-sm font-medium text-gray-900">
                  {{ authStore.currentUser.firstName }} {{ authStore.currentUser.lastName }}
                </p>
                <p class="text-xs text-gray-500">{{ authStore.currentUser.email }}</p>
              </div>
              <div
                class="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-semibold text-sm"
              >
                {{ authStore.currentUser.firstName?.charAt(0) || 'U' }}
              </div>
              <button
                @click="authStore.logout"
                class="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                title="Logout"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            </div>
            <div v-else class="flex items-center space-x-3">
              <div class="text-right">
                <p class="text-sm font-medium text-gray-900">Loading...</p>
              </div>
              <div class="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      </header>

      <!-- Main Content -->
      <main class="bg-gray-50" :class="{ 'p-8': !noSpacing }">
        <slot />
      </main>
    </div>
  </div>
</template>
